import { onError } from "@apollo/client/link/error";
import { logger, NetworkState } from "@mwt/web-js-telemetry";
import moment, { duration } from "moment";

import {
  DataBagHttpColumns,
  EventType,
  TelemetryErrorStatus,
} from "@smb/web-telemetry";
import { ErrorTypes } from "../../shared/errorTypes";

import { LogTelemetryErrorEventParams } from "./telemetryErrorLink.types";

/**
 * Logs network, data/graphql errors to telemetry.
 */
export const telemetryErrorLink = onError(
  ({ operation, graphQLErrors, networkError }) => {
    const { operationName } = operation;
    const context = operation.getContext();
    const { eventStartTime } = context;
    const endTime = moment();

    const durationInMs = eventStartTime
      ? duration(endTime.diff(eventStartTime)).asMilliseconds()
      : 0;
    const requestHeaders = context?.headers;
    const correlationId = requestHeaders?.["x-ms-correlationid"];
    const responseObject = context?.response;
    const resultCode = responseObject?.statusCode;
    const contentLength = responseObject?.headers?.get("content-length");
    const url = responseObject?.url ?? "";

    // Apparently it is very difficult to simulate network errors witout
    // the use of MockProvider (which we cant use to test a link)
    // so ignoring the else section.
    /* istanbul ignore else */
    if (graphQLErrors) {
      for (const graphqlError of graphQLErrors) {
        const errorType: string | undefined =
          graphqlError.extensions?.data?.errorType;
        const errorSubType: string | undefined =
          graphqlError.extensions?.data?.errorSubType;

        logTelemetryErrorEvent({
          durationInMs,
          correlationId,
          operationName,
          error: graphqlError,
          requestResult: TelemetryErrorStatus.Error,
          eventStartTime,
          resultCode,
          contentLength,
          url,
          errorType,
          errorSubType,
        });

        // Needed as an additional check to handle error redirect
        // for license not found errors thrown by any of the APIs during setup.
        if (
          errorSubType?.toLocaleLowerCase() === ErrorTypes.LICENSE_NOT_FOUND
        ) {
          window.location.replace(
            `/error?error=${ErrorTypes.LICENSE_NOT_FOUND}`
          );
        }
      }
    }

    /**
     * Ignoring the else path here since this link doesnt get executed when
     * neither networkError nor graphQlErrors exist.
     */
    /* istanbul ignore else */
    if (networkError) {
      logTelemetryErrorEvent({
        durationInMs,
        correlationId,
        operationName,
        error: networkError,
        requestResult: TelemetryErrorStatus.NetworkError,
        eventStartTime,
        resultCode,
        contentLength,
        url,
      });
    }
  }
);

const logTelemetryErrorEvent = ({
  durationInMs,
  correlationId,
  operationName,
  error,
  requestResult,
  eventStartTime,
  resultCode,
  contentLength,
  url,
  errorType = "",
  errorSubType = "",
}: LogTelemetryErrorEventParams) => {
  logger.logHttpRequestEvent(
    {
      hostname: window.location.hostname,
      duration: durationInMs,
      requestResult,
      resultCode,
      contentLength,
      requestCreatedTime: moment(eventStartTime).utc().format(),
      networkState: window.navigator?.onLine
        ? NetworkState.Online
        : NetworkState.Offline,
      databag: {
        [DataBagHttpColumns.DataBagIsSuccessful]: false,
        [DataBagHttpColumns.DataBagCorrelationId]: correlationId,
        [DataBagHttpColumns.DataBagUrl]: operationName,
        [DataBagHttpColumns.DataBagReason]: error.message,
        [DataBagHttpColumns.DataBagServiceUrl]: url,
        [DataBagHttpColumns.DataBagErrorType]: errorType,
        [DataBagHttpColumns.DatabBagErrorSubType]: errorSubType,
      },
    },
    EventType.NonUiEvent
  );
};
